import {getSizeCommerceView, isBasketLineAvailable} from './onecStockAvailability';
import {isServerSnapshotTimeValid} from './serverSnapshotTime';

export const BASKET_IDENTITY_SCHEMA = 'site-basket-identity-v1';
export const BASKET_RECOVERY_ERROR = 'Не удалось проверить сохранённую корзину.';
export const BASKET_SNAPSHOT_ERROR = 'Не удалось обновить корзину.';
const INTEGER_MAX = 2147483647;
const record = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const id = value => Number.isInteger(value) && value > 0 && value <= INTEGER_MAX;
const size = value => typeof value === 'string' && value.length > 0 && value.length <= 128 && /\S/.test(value);
const iso = value => typeof value === 'string' && /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d\.\d{3}Z$/.test(value) &&
  Number.isFinite(Date.parse(value)) && new Date(Date.parse(value)).toISOString() === value;
const dense = value => Array.isArray(value) && value.length <= 500 && Object.keys(value).length === value.length &&
  Array.from({length: value.length}, (_, index) => Object.prototype.hasOwnProperty.call(value, index)).every(Boolean);
const fail = message => {throw new Error(message);};
const clone = value => JSON.parse(JSON.stringify(value));
const sameValue = (left, right) => {
  if (left === right) return true;
  if (Array.isArray(left) && Array.isArray(right)) return left.length === right.length &&
    left.every((value, index) => sameValue(value, right[index]));
  if (!record(left) || !record(right)) return false;
  const keys = Object.keys(left);
  return keys.length === Object.keys(right).length && keys.every(key =>
    Object.prototype.hasOwnProperty.call(right, key) && sameValue(left[key], right[key]));
};

export function validateBasketSession(session) {
  if (!record(session) || !['guest', 'authenticated'].includes(session.authScope) ||
      (session.authScope === 'guest' ? session.principalId !== null : !id(session.principalId))) {
    fail(BASKET_SNAPSHOT_ERROR);
  }
  return {authScope: session.authScope, principalId: session.principalId};
}

export function basketIdentityItems(items) {
  if (!dense(items)) fail(BASKET_RECOVERY_ERROR);
  return items.map(line => {
    if (!record(line)) fail(BASKET_RECOVERY_ERROR);
    const childId = Object.prototype.hasOwnProperty.call(line, 'child_product_id') ? line.child_product_id : line.item?.id;
    if (!id(childId) || !size(line.currentSize) || !id(line.count) ||
        (line.item?.id !== undefined && line.item.id !== childId)) fail(BASKET_RECOVERY_ERROR);
    const result = {child_product_id: childId, currentSize: line.currentSize, count: line.count};
    for (const key of ['productId', 'colorId', 'sizeId']) {
      if (!Object.prototype.hasOwnProperty.call(line, key)) continue;
      const value = line[key];
      if (value === null || value === undefined) continue;
      if (!id(value)) fail(BASKET_RECOVERY_ERROR);
      result[key] = value;
    }
    return result;
  });
}

// Reading recovery never writes, empties, repairs, or grants a cached card.
export function readBasketRecovery(raw, session) {
  const current = validateBasketSession(session);
  if (raw === null || raw === undefined) return {provenance: 'none', items: []};
  if (typeof raw !== 'string' || raw.length > 1024 * 1024) fail(BASKET_RECOVERY_ERROR);
  let stored;
  try {stored = JSON.parse(raw);} catch {fail(BASKET_RECOVERY_ERROR);}
  if (Array.isArray(stored)) return {provenance: 'legacy_unscoped', items: basketIdentityItems(stored)};
  if (!record(stored) || stored.schemaVersion !== BASKET_IDENTITY_SCHEMA) fail(BASKET_RECOVERY_ERROR);
  let saved;
  try {saved = validateBasketSession(stored);} catch {fail(BASKET_RECOVERY_ERROR);}
  const items = basketIdentityItems(stored.items);
  if (saved.authScope === 'guest') return {provenance: 'guest', items};
  if (current.authScope === 'authenticated' && saved.principalId === current.principalId) {
    return {provenance: 'same_owner', items};
  }
  return {provenance: 'foreign_owner', items: []};
}

export function serializeBasketIdentities(items, session) {
  return JSON.stringify({schemaVersion: BASKET_IDENTITY_SCHEMA, ...validateBasketSession(session), items: basketIdentityItems(items)});
}

const validLineKey = line => {
  if (typeof line.lineKey !== 'string') return false;
  const persisted = /^basket:([1-9]\d*)$/.exec(line.lineKey);
  if (persisted) return id(Number(persisted[1]));
  const prefix = `sku:${line.child_product_id}:${encodeURIComponent(line.currentSize)}:`;
  const occurrence = line.lineKey.slice(prefix.length);
  return line.lineKey.startsWith(prefix) && /^(0|[1-9]\d*)$/.test(occurrence) && Number(occurrence) < 500;
};

// Envelope validation is not a second commerce evaluator. The existing helper
// validates the descriptor/field binding; shadow is inspected as enforced only
// for structural validation, never to grant shadow purchases.
export function normalizeBasketSnapshot(envelope, session) {
  const current = validateBasketSession(session);
  if (!record(envelope) || envelope.error || envelope.noAuth || !dense(envelope.items)) fail(BASKET_SNAPSHOT_ERROR);
  const metadata = envelope.publicSku;
  let mode = 'legacy';
  if (metadata !== undefined) {
    if (!record(metadata) || metadata.schemaVersion !== 'public-sku-v1' ||
        !['shadow', 'enforced'].includes(metadata.mode) || !iso(metadata.evaluatedAt) ||
        !isServerSnapshotTimeValid(metadata.evaluatedAt) ||
        envelope.scope !== current.authScope) fail(BASKET_SNAPSHOT_ERROR);
    mode = metadata.mode;
  } else if (envelope.scope !== undefined || envelope.items.some(line => line?.item?.commerce !== undefined)) {
    fail(BASKET_SNAPSHOT_ERROR);
  }
  const keys = new Set(), occurrences = new Map();
  const items = envelope.items.map(raw => {
    const identity = basketIdentityItems([raw])[0];
    let lineKey = raw.lineKey;
    const hydratedShadow = mode === 'shadow' && raw.lineKey === undefined && raw.status === undefined && raw.sku === undefined;
    if (mode === 'legacy' || hydratedShadow) {
      if (!record(raw.item)) fail(BASKET_SNAPSHOT_ERROR);
      if (hydratedShadow && raw.item.commerce !== undefined) {
        const commerce = raw.item.commerce;
        if (!record(commerce) || commerce.mode !== 'shadow' || commerce.schemaVersion !== metadata.schemaVersion ||
            commerce.evaluatedAt !== metadata.evaluatedAt || !getSizeCommerceView({...raw.item,
              commerce: {...commerce, mode: 'enforced'}}, raw.currentSize, raw.count).descriptor) fail(BASKET_SNAPSHOT_ERROR);
      }
      const tuple = `${identity.child_product_id}:${encodeURIComponent(identity.currentSize)}:${identity.colorId ?? 'none'}`;
      const occurrence = occurrences.get(tuple) || 0;
      occurrences.set(tuple, occurrence + 1);
      lineKey = `legacy:${tuple}:${occurrence}`;
    } else {
      if (!id(raw.child_product_id) || !validLineKey(raw) || !['visible', 'unavailable'].includes(raw.status)) fail(BASKET_SNAPSHOT_ERROR);
      if (raw.status === 'unavailable') {
        if (raw.item !== null || raw.reasonCode !== 'item_unavailable' || raw.sku !== undefined) fail(BASKET_SNAPSHOT_ERROR);
      } else {
        const child = raw.item, commerce = child?.commerce;
        if (!record(child) || child.id !== identity.child_product_id ||
            !id(child.productId) || !id(child.colorId) || raw.productId !== child.productId || raw.colorId !== child.colorId ||
            child.product?.id !== child.productId || typeof child.product?.electron_sertificate !== 'boolean' ||
            (child.color !== undefined && child.color?.id !== child.colorId) ||
            !record(commerce) || commerce.schemaVersion !== metadata.schemaVersion ||
            commerce.mode !== mode || commerce.evaluatedAt !== metadata.evaluatedAt) fail(BASKET_SNAPSHOT_ERROR);
        const inspected = mode === 'shadow' ? {...child, commerce: {...commerce, mode: 'enforced'}} : child;
        const descriptor = getSizeCommerceView(inspected, raw.currentSize, raw.count).descriptor;
        if (!descriptor || !sameValue(raw.sku, descriptor)) fail(BASKET_SNAPSHOT_ERROR);
      }
    }
    if (keys.has(lineKey)) fail(BASKET_SNAPSHOT_ERROR);
    keys.add(lineKey);
    return {...clone(raw), lineKey};
  });
  return {items, mode, evaluatedAt: metadata?.evaluatedAt || null};
}

const skuKey = line => `${line.child_product_id ?? line.item?.id}:${encodeURIComponent(line.currentSize)}`;
export function selectedBasketPurchaseLines(items, locked = false) {
  if (locked) return [];
  const selected = items.filter(line => line.selected !== false && isBasketLineAvailable(line));
  const groups = new Map();
  for (const line of selected) {
    if (line.item?.product?.electron_sertificate === true) continue;
    const key = skuKey(line);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(line);
  }
  const blocked = new Set();
  for (const [key, group] of groups) {
    const quantity = group.reduce((sum, line) => sum + line.count, 0);
    if (!group.every(line => isBasketLineAvailable({...line, count: quantity}))) blocked.add(key);
  }
  return selected.filter(line => line.item?.product?.electron_sertificate === true || !blocked.has(skuKey(line)));
}

export function canIncreaseBasketLine(items, lineKey, locked = false) {
  if (locked) return false;
  const line = items.find(item => item.lineKey === lineKey);
  if (!line || !isBasketLineAvailable(line)) return false;
  const quantity = items.filter(item => skuKey(item) === skuKey(line)).reduce((sum, item) => sum + item.count, 0) + 1;
  return quantity <= INTEGER_MAX && isBasketLineAvailable({...line, count: quantity});
}

export function nextBasketExpiry(items, now = Date.now()) {
  let nearest = null;
  for (const line of items) {
    const expiry = getSizeCommerceView(line.item, line.currentSize, line.count).expiresAt;
    if (expiry !== null && expiry >= now && (nearest === null || expiry < nearest)) nearest = expiry;
  }
  return nearest;
}
